import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

export default class extends Controller {
  static targets = ["indicator", "icon"]

  connect() {
    this.reset()
  }

  disconnect() {
    this.abortController?.abort()
    delete this.element.dataset.pullRefreshEnabled
  }

  start(event) {
    if (this.refreshing) return
    this.reset()
    if (event.touches.length !== 1 || window.scrollY > 0 || document.querySelector("dialog[open]")) return
    if (event.target.closest("input, select, textarea, [contenteditable]")) return

    const surface = event.target.closest(".main-container")
    if (!surface) return

    for (let node = event.target; node !== surface; node = node.parentElement) {
      if (/(auto|scroll)/.test(getComputedStyle(node).overflowY) && node.scrollHeight > node.clientHeight) return
      if (/(auto|scroll)/.test(getComputedStyle(node).overflowX) && node.scrollWidth > node.clientWidth) return
    }

    this.origin = { x: event.touches[0].clientX, y: event.touches[0].clientY }
    this.positionIndicator()
  }

  move(event) {
    if (!this.origin || this.refreshing) return
    if (event.touches.length !== 1 || !event.cancelable) return this.reset()

    const dx = event.touches[0].clientX - this.origin.x
    const dy = event.touches[0].clientY - this.origin.y
    if (!this.pulling && (dy < 0 || Math.abs(dx) > Math.abs(dy))) return this.reset()
    if (!this.pulling && dy < 8) return
    if (window.scrollY > 0) return this.reset()

    event.preventDefault()
    this.pulling = true
    this.ready = dy >= 120
    this.indicatorTarget.hidden = false
    this.indicatorTarget.dataset.state = this.ready ? "ready" : "pulling"
    this.indicatorTarget.style.setProperty("--pull-offset", `${40 * (1 - Math.exp(-Math.max(0, dy) / 160))}px`)
    this.indicatorTarget.style.setProperty("--pull-opacity", 0.35 + 0.65 * Math.min(1, Math.max(0, dy) / 120))
    this.iconTarget.style.setProperty("--pull-rotation", `${Math.min(180, Math.max(0, dy) * 1.5)}deg`)
  }

  end(event) {
    if (this.refreshing) return
    if (this.pulling) event.preventDefault()
    if (this.ready) this.refresh()
    else this.reset()
  }

  cancel() {
    if (!this.refreshing) this.reset()
  }

  async refresh() {
    if (this.refreshing) return

    this.refreshing = true
    this.origin = null
    this.positionIndicator()
    this.indicatorTarget.hidden = false
    this.indicatorTarget.dataset.state = "loading"
    this.indicatorTarget.style.removeProperty("--pull-offset")

    const url = window.location.href
    const request = new AbortController()
    this.abortController = request

    try {
      const response = await fetch(url, {
        cache: "no-cache",
        headers: { Accept: "text/html" },
        signal: request.signal
      })
      if (!response.ok || !response.headers.get("Content-Type")?.includes("text/html")) {
        throw new Error("Unable to refresh page")
      }

      const responseHTML = await response.text()
      if (request.signal.aborted || window.location.href !== url) return

      this.abortController = null
      Turbo.cache.clear()
      Turbo.visit(response.redirected ? response.url : url, {
        action: "replace",
        response: { statusCode: response.status, responseHTML, redirected: response.redirected }
      })
    } catch {
      if (request.signal.aborted) return

      this.reset()
      this.indicatorTarget.hidden = false
      this.indicatorTarget.dataset.state = "error"
    }
  }

  positionIndicator() {
    const header = document.querySelector(".public-header")
    this.indicatorTarget.style.top = `${header.getBoundingClientRect().bottom + 8}px`
  }

  reset() {
    this.abortController?.abort()
    this.abortController = null
    this.element.dataset.pullRefreshEnabled = "true"
    this.origin = null
    this.pulling = false
    this.ready = false
    this.refreshing = false
    this.indicatorTarget.hidden = true
    this.indicatorTarget.style.removeProperty("--pull-offset")
    this.indicatorTarget.style.removeProperty("--pull-opacity")
  }
}
